import { palette } from '../styles/constants';
import { PayoutSplit, OrderItem, PayoutFeeSplit } from '../types/checkout';

// Shared palette used for split payments
const splitsPalette = [
    palette.primary[500],
    palette.primary[400],
    palette.primary[300],
    palette.primary[200],
    palette.warning[500],
    palette.warning[400],
    palette.warning[300],
    palette.warning[200],
    palette.success[500],
    palette.success[400],
    palette.success[300],
    palette.success[200],
    palette.destructive[500],
    palette.destructive[400],
    palette.destructive[300],
    palette.destructive[200],
    palette.neutral[500],
    palette.neutral[400],
    palette.neutral[300],
    palette.neutral[200],
];

export const getSplitPaymentColor = (index: number): string => {
    const paletteIndex = index % splitsPalette.length;
    return splitsPalette[paletteIndex] || 'transparent';
};

export const getSkewedSplits = (splits: PayoutSplit[], ratio: number, amount: number) => {
    const skewedSplits = splits.map(split => {
        const splitAmount = Math.floor(split.amount * ratio);
        return {
            ...split,
            amount: splitAmount,
        };
    }).filter(split => split.amount !== 0);

    const total = skewedSplits.reduce((acc, split) => acc + split.amount, 0);
    const remainder = amount - total;
    // TODO: fix this later
    // current workaround always give the remainder to the last split
    console.log({ remainder });
    if (remainder && skewedSplits.length !== 0) {
        skewedSplits[skewedSplits.length - 1].amount += remainder;
    }
    return skewedSplits;
};

export const deduplicateSplits = (splits: PayoutSplit[]) => {
    return splits.reduce<PayoutSplit[]>((acc, split) => {

        const existingSplit = acc.find(s => s.payout_destination_id === split.payout_destination_id);
        if (existingSplit) {
            // mutate copied split in acc
            existingSplit.amount += split.amount;
        } else {
            // copy new split so that we don't mutate the original split
            acc.push({ ...split });
        }
        return acc;
    }, []);
};

export const getOrderItemsSplits = (items: OrderItem[], lineId: string) => {
    // Not sure if the same items can be captured multiple times within a capture so
    // we assume it is possible.

    return items.reduce<PayoutSplit[]>((acc, eventItem) => {
        if (eventItem.line_id === lineId) {
            return [...acc, ...(eventItem.splits || [])];
        }
        return acc;
    }, []);
};

export const subtractRefundedFromCapturedSplits = (capturedSplits: PayoutSplit[], refundedSplits: PayoutSplit[]) => {
    const refundableSplits = capturedSplits.reduce<PayoutSplit[]>((acc, split) => {
        const refundedSplit = refundedSplits.find(s => s.payout_destination_id === split.payout_destination_id);
        if (refundedSplit) {
            split.amount -= refundedSplit.amount;
        }
        if (split.amount > 0) {
            acc.push(split);
        }
        return acc;
    }, []);

    return refundableSplits;
};

export const filterEmptySplits = (splits: PayoutSplit[]) => splits.filter(split => split.amount !== 0);

export const filterEmptySplitsInFeeSplit = (splits: PayoutSplit[], feeSplitToggles: boolean[]): PayoutFeeSplit => {
    const destinations = splits.reduce<string[]>((acc, split, index) => {
        if (split.amount > 0 && feeSplitToggles[index]) {
            return [...acc, split.payout_destination_id];
        }
        return acc;
    }, []);
    return {
        type: 'proportional',
        destinations,
    };
};

export const replaceSplitAt = (splits: PayoutSplit[], newSplit: PayoutSplit, index: number) => {
    return [...splits.slice(0, index), newSplit, ...splits.slice(index + 1)];
};

export const getDefaultFeeSplit = (splits: PayoutSplit[]) => (
    splits.reduce<PayoutFeeSplit>((acc, split) => ({
        ...acc,
        destinations: [...(acc.destinations || []), split.payout_destination_id], 
    }), { destinations: [], type: 'proportional' })
);

export const checkPayoutSplitsValid = (amount: number, payoutSplits: PayoutSplit[]) => {
    const newTotal = payoutSplits.reduce((sum, p) => sum + p.amount, 0);
    return newTotal === amount;
};

export const checkFeeSplitValid = (payoutSplits: PayoutSplit[], feeSplitToggles: boolean[]) => {
    const feeIsValid = feeSplitToggles.find((t, i) => t && payoutSplits[i].amount > 0);
    return !!feeIsValid;
};
